import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { auditMafertAppMigrations, getLatestMafertAppMigrationAudit } from '../api/tenantSchemaApi';
import { DataTable } from '../components/DataTable';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { PageShell } from '../components/PageShell';
import { StatusBadge } from '../components/StatusBadge';

export function TenantSchemaAuditPage() {
  const { t } = useTranslation(['tenantSchema', 'common']);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getLatestMafertAppMigrationAudit();
      setAudit(response.data || response);
    } catch {
      setAudit(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAudit = async () => {
    setRunning(true);
    try {
      const response = await auditMafertAppMigrations();
      setAudit(response.data || response);
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  };

  const migrations = audit?.migrations || [];
  const summary = audit?.summary || {};

  const columns = useMemo(() => [
    { id: 'migration_name', label: t('tenantSchema:audit.columns.migration') },
    { id: 'category', label: t('tenantSchema:audit.columns.category') },
    { id: 'risk_level', label: t('tenantSchema:audit.columns.risk'), render: (row) => <StatusBadge status={row.risk_level} category="schema_risk" /> },
    { id: 'include_candidate', label: t('tenantSchema:audit.columns.include'), render: (row) => (row.include_candidate ? t('statuses:yes') : t('statuses:no')) },
  ], [t]);

  if (loading) return <LoadingState />;

  return (
    <PageShell title={t('tenantSchema:audit.title')} subtitle={t('tenantSchema:audit.subtitle')}>
      <Alert severity="info" sx={{ mb: 2 }}>{t('tenantSchema:warnings.audit_read_only')}</Alert>
      {error && <ErrorState message={t('tenantSchema:audit.run_failed')} onRetry={runAudit} />}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={runAudit} disabled={running}>{t('tenantSchema:actions.run_audit')}</Button>
      </Stack>
      {summary.total != null && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('tenantSchema:audit.summary', { total: summary.total, include: summary.include_candidates, excluded: summary.excluded })}
        </Typography>
      )}
      <DataTable columns={columns} rows={migrations} rowKey="artifact_key" emptyMessage={t('tenantSchema:audit.no_results')} />
    </PageShell>
  );
}
